import { extractErrorMessage, useGetTransportGaugeQuantityAuditsQuery, usePreviewTransportGaugeQuantityMutation } from "@ots-frontend/ots-data";
import { skipToken } from "@reduxjs/toolkit/query";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import { GaugeQuantityAuditDialog } from "../GaugeQuantityAuditDialog";
import type { GaugeQuantityAudit, GaugeQuantityResult } from "../gauge-quantity-types";

export type ConnectedGaugeQuantityAuditDialogProps = {
  open: boolean;
  onClose: () => void;
  transportId: string;
  database?: string;
  /** E.g. "Gauge quantity · Pickup · WG-1001". */
  title?: string;
  compact?: boolean;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** How a transport's gauge quantity was derived, or why it was not, with a dry-run recalculation. */
export const ConnectedGaugeQuantityAuditDialog = ({
  open,
  onClose,
  transportId,
  database,
  title,
  compact,
  brand = "water-go",
  colorScheme = "system",
}: ConnectedGaugeQuantityAuditDialogProps) => {
  const { data, error, isFetching, refetch } = useGetTransportGaugeQuantityAuditsQuery(open ? { transportId, database } : skipToken);
  const [preview, previewState] = usePreviewTransportGaugeQuantityMutation();

  return (
    <GaugeQuantityAuditDialog
      open={open}
      onClose={onClose}
      title={title}
      compact={compact}
      audits={data as GaugeQuantityAudit[] | undefined}
      loading={isFetching && !data}
      error={error ? extractErrorMessage(error, "The gauge quantity history could not be loaded.") : undefined}
      onRetry={() => void refetch()}
      onPreview={() => void preview({ transportId, database })}
      preview={previewState.data as GaugeQuantityResult | undefined}
      previewing={previewState.isLoading}
      previewError={previewState.error ? extractErrorMessage(previewState.error, "The gauge quantity could not be calculated.") : undefined}
      brand={brand}
      colorScheme={colorScheme}/>
  );
};
